// Shift-based Planning Utilities for Payroll Calculations

import { supabase } from '@/integrations/supabase/client';
import { Employee, Role } from '@/types';
import { startOfMonth, endOfMonth, parseISO, differenceInMinutes, format } from 'date-fns';

export interface ScheduledShift {
  id: string;
  employee_id: string | null;
  employee_name: string | null;
  start_time: string;
  end_time: string;
  title: string | null;
  duration_minutes: number;
}

export interface EmployeePlannedHours {
  employee_id: string;
  employee_name: string;
  pay_type: 'hourly' | 'salary' | 'fixed_contract';
  contracted_hours_per_month?: number;
  default_pay_rate?: number;
  planned_hours: number;
  scheduled_shifts: ScheduledShift[];
  variance_from_contracted?: number; // For salary employees
  compliance_status: 'over_scheduled' | 'under_scheduled' | 'compliant' | 'not_applicable';
  warnings: string[];
}

export interface PlanningPeriod {
  start_date: Date;
  end_date: Date;
  year: number;
  month: number;
}

/**
 * Get all scheduled shifts for a business within a date range
 */
export async function getScheduledShiftsForPeriod(
  businessId: string,
  startDate: Date,
  endDate: Date
): Promise<ScheduledShift[]> {
  const { data: shifts, error } = await supabase
    .from('shifts')
    .select(`
      id,
      employee_id,
      start_time,
      end_time,
      title,
      employees(name)
    `)
    .eq('business_id', businessId)
    .gte('start_time', startDate.toISOString())
    .lte('end_time', endDate.toISOString())
    .order('start_time');

  if (error) {
    throw new Error(`Failed to fetch scheduled shifts: ${error.message}`);
  }

  return shifts.map(shift => ({
    id: shift.id,
    employee_id: shift.employee_id,
    employee_name: shift.employees?.name || null,
    start_time: shift.start_time,
    end_time: shift.end_time,
    title: shift.title,
    duration_minutes: differenceInMinutes(
      parseISO(shift.end_time),
      parseISO(shift.start_time)
    )
  }));
}

/**
 * Calculate planned hours for all employees in a given period
 */
export async function calculatePlannedHoursForPeriod(
  businessId: string,
  year: number,
  month: number
): Promise<EmployeePlannedHours[]> {
  const periodStart = startOfMonth(new Date(year, month - 1, 1));
  const periodEnd = endOfMonth(periodStart);
  
  // Get all employees for the business with their roles
  const { data: employees, error: employeesError } = await supabase
    .from('employees')
    .select(`
      id,
      name,
      role_id,
      roles(
        name,
        pay_type,
        hours_per_month,
        default_pay_rate
      )
    `)
    .eq('business_id', businessId);

  if (employeesError) {
    throw new Error(`Failed to fetch employees: ${employeesError.message}`);
  }

  // Get all scheduled shifts for the period
  const scheduledShifts = await getScheduledShiftsForPeriod(businessId, periodStart, periodEnd);

  // Group shifts by employee and calculate planned hours
  const employeePlannedHours: EmployeePlannedHours[] = employees.map(employee => {
    const role = employee.roles;
    const employeeShifts = scheduledShifts.filter(shift => shift.employee_id === employee.id);
    
    const totalMinutes = employeeShifts.reduce((sum, shift) => sum + shift.duration_minutes, 0);
    const plannedHours = totalMinutes / 60;
    
    // Calculate variance for salary employees
    let varianceFromContracted: number | undefined;
    let complianceStatus: EmployeePlannedHours['compliance_status'] = 'not_applicable';
    const warnings: string[] = [];
    
    if (role?.pay_type === 'salary' && role.hours_per_month) {
      varianceFromContracted = plannedHours - role.hours_per_month;
      
      if (varianceFromContracted > 0) {
        complianceStatus = 'over_scheduled';
        warnings.push(`Over-scheduled by ${varianceFromContracted.toFixed(1)} hours this month`);
      } else if (varianceFromContracted < -4) { // Allow 4 hour tolerance
        complianceStatus = 'under_scheduled';
        warnings.push(`Under-scheduled by ${Math.abs(varianceFromContracted).toFixed(1)} hours this month`);
      } else {
        complianceStatus = 'compliant';
      }
    } else if (role?.pay_type === 'fixed_contract' && role.hours_per_month) {
      varianceFromContracted = plannedHours - role.hours_per_month;
      complianceStatus = varianceFromContracted === 0 ? 'compliant' : 
                        varianceFromContracted > 0 ? 'over_scheduled' : 'under_scheduled';
    }
    
    return {
      employee_id: employee.id,
      employee_name: employee.name,
      pay_type: role?.pay_type || 'hourly',
      contracted_hours_per_month: role?.hours_per_month || undefined,
      default_pay_rate: role?.default_pay_rate || undefined,
      planned_hours: plannedHours,
      scheduled_shifts: employeeShifts,
      variance_from_contracted: varianceFromContracted,
      compliance_status: complianceStatus,
      warnings: warnings
    };
  });

  return employeePlannedHours;
}

/**
 * Get planning data for a specific employee in a given period
 */
export async function getEmployeePlannedHours(
  businessId: string,
  employeeId: string,
  year: number,
  month: number
): Promise<EmployeePlannedHours | null> {
  const allPlannedHours = await calculatePlannedHoursForPeriod(businessId, year, month);
  return allPlannedHours.find(eph => eph.employee_id === employeeId) || null;
}

/**
 * Calculate projected gross pay based on planned hours
 */
export function calculateProjectedGrossPay(plannedHours: EmployeePlannedHours): number {
  const { pay_type, planned_hours, default_pay_rate, contracted_hours_per_month } = plannedHours;
  
  if (!default_pay_rate) return 0;
  
  switch (pay_type) {
    case 'hourly':
      return planned_hours * default_pay_rate;
      
    case 'salary':
    case 'fixed_contract': {
      if (!contracted_hours_per_month) return default_pay_rate; // Assume monthly salary

      const monthlyBaseSalary = default_pay_rate;
      const overtimeHours = Math.max(0, planned_hours - contracted_hours_per_month);
      const overtimeRate = (default_pay_rate / contracted_hours_per_month) * 1.5; // 150% for overtime

      return monthlyBaseSalary + (overtimeHours * overtimeRate);
    }

    default:
      return 0;
  }
}

/**
 * Get planning summary for the entire business for a given period
 */
export async function getBusinessPlanningSummary(
  businessId: string,
  year: number,
  month: number
) {
  const plannedHours = await calculatePlannedHoursForPeriod(businessId, year, month);
  
  const totalEmployees = plannedHours.length;
  const employeesWithShifts = plannedHours.filter(eph => eph.planned_hours > 0).length;
  const overScheduledEmployees = plannedHours.filter(eph => eph.compliance_status === 'over_scheduled').length;
  const underScheduledEmployees = plannedHours.filter(eph => eph.compliance_status === 'under_scheduled').length;
  
  const totalPlannedHours = plannedHours.reduce((sum, eph) => sum + eph.planned_hours, 0);
  const totalProjectedGrossPay = plannedHours.reduce((sum, eph) => sum + calculateProjectedGrossPay(eph), 0);
  
  const complianceIssues = plannedHours
    .filter(eph => eph.warnings.length > 0)
    .map(eph => ({
      employee_name: eph.employee_name,
      warnings: eph.warnings
    }));
  
  return {
    period: { year, month },
    totalEmployees,
    employeesWithShifts,
    overScheduledEmployees,
    underScheduledEmployees,
    totalPlannedHours: Math.round(totalPlannedHours * 100) / 100,
    totalProjectedGrossPay: Math.round(totalProjectedGrossPay * 100) / 100,
    complianceIssues,
    plannedHoursDetails: plannedHours
  };
}